/* eslint-disable @typescript-eslint/no-explicit-any -- DIRECT-239681 */

import { useMemo } from 'react';
import { skipToken as flowSkipToken } from '@adv-frontend/modules-flow';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { stableSerialize } from '@direct-frontend/stdlib';

import { createQueryHook } from '../createQueryHook';
import { useQuery, type AdditionalUseQueryOptions } from './useQuery';
import type { AnyApi } from '../buildCreateApi';
import type { QueryArgFrom } from '../rtk-query-types';
import type { EndpointQueryFlow } from './buildEndpointQueryFlow';
import type { AnyEndpointDefinition, AnyQueryDefinition, UseQueryResult, UseQuerySubscriptionOptions } from './types';

/**
 * Проверяет является ли EndpointDefinition QueryDefinition.
 *
 * @internal
 */
export function isQueryDefinition(endpointDefinition: AnyEndpointDefinition): endpointDefinition is AnyQueryDefinition {
    return endpointDefinition.type === 'query';
}

// Параметры для `buildSSRCompatibleQueryHook()`.
type BuildSSRCompatibleQueryHookOptions = {
    api: AnyApi;
    endpointName: string;
};

type QueryOptions = UseQuerySubscriptionOptions & AdditionalUseQueryOptions;

// Метод для генерации useQuery хука, привязанного к эндпоинту.
// Хук — тонкая обёртка над стэндалон `useQuery()`: собирает поток из (args, options)
// и делегирует подписку и формирование результата стэндалону.
export function buildSSRCompatibleQueryHook({ api, endpointName }: BuildSSRCompatibleQueryHookOptions) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const endpoint = api.endpoints[endpointName] as unknown as EndpointQueryFlow<any>;

    return createQueryHook(function useSsrCompatibleQuery(
        queryArgs: QueryArgFrom<AnyQueryDefinition>,
        queryOptions?: QueryOptions
    ): UseQueryResult<AnyQueryDefinition> {
        if (process.env.NODE_ENV !== 'production' && queryOptions?.refetchOnMountOrArgChange) {
            throw new Error('Опция refetchOnMountOrArgChange не поддерживается в модульных сторах.');
        }

        const isSkipped = queryArgs === skipToken || Boolean(queryOptions?.skip);
        const pollingInterval = queryOptions?.pollingInterval;
        const skipPollingIfUnfocused = queryOptions?.skipPollingIfUnfocused;
        const refetchOnFocus = queryOptions?.refetchOnFocus;
        const refetchOnReconnect = queryOptions?.refetchOnReconnect;

        // Ссылка на поток — часть контракта `useQuery`: она в deps у `refetch`, по ней сравниваются
        // ошибки и переподписывается `useAsyncFlow`. Вдобавок вызов на каждый рендер через
        // `onCacheHit` взводил бы лишний `dispatch(initiate)`. Ключ — стабильная сериализация,
        // потому что сами `queryArgs` приходят новым объектом.
        const flowCacheKey = useMemo(
            () =>
                isSkipped
                    ? 'SKIP'
                    : stableSerialize({
                          args: queryArgs,
                          options: { pollingInterval, skipPollingIfUnfocused, refetchOnFocus, refetchOnReconnect },
                      }),
            [isSkipped, queryArgs, pollingInterval, skipPollingIfUnfocused, refetchOnFocus, refetchOnReconnect]
        );
        const queryFlow = useMemo(
            () =>
                isSkipped
                    ? flowSkipToken
                    : endpoint.queryFlow(queryArgs, {
                          pollingInterval,
                          skipPollingIfUnfocused,
                          refetchOnFocus,
                          refetchOnReconnect,
                      }),
            // eslint-disable-next-line react-hooks/exhaustive-deps -- flowCacheKey покрывает args + options
            [isSkipped, flowCacheKey]
        );

        const result = useQuery(queryFlow, { keepPrefetchedDataFor: queryOptions?.keepPrefetchedDataFor });

        // В скип-режиме стэндалон-хук не знает аргументов запроса — подменяем `originalArgs`,
        // чтобы сохранить совместимость с публичным API эндпоинтного `useQuery`,
        // который всегда отдает переданные пользователем аргументы.
        if (queryFlow === flowSkipToken) {
            return { ...result, originalArgs: queryArgs };
        }

        return result;
    });
}
